import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import AuthShell from "./AuthShell";

export default function Login({ portal: fixed }) {
  const params = useParams();
  const portal = fixed || params.role || "student";
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isAdmin = portal === "admin";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(f.identifier, f.password, portal);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.must_change_password ? "/admin/change-password" : homeFor(u));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={isAdmin ? "Admin login" : `${portal === "tutor" ? "Tutor" : "Student"} login`}
      subtitle={isAdmin ? "Restricted to FosterApp administrators." : "Sign in to your FosterApp dashboard."}
      footer={!isAdmin && (
        <div className="space-y-2">
          <p>New here? <Link className="font-semibold text-[#1F6FFF]" to={`/register/${portal}`} data-testid="login-register-link">Create a {portal} account</Link></p>
          <p>{portal === "student" ? "Are you a tutor? " : "Are you a student? "}
            <Link className="font-semibold text-[#1F6FFF]" to={`/login/${portal === "student" ? "tutor" : "student"}`} data-testid="login-switch-portal-link">Switch login</Link></p>
        </div>
      )}>
      {!isAdmin && (
        <div className="mb-6 grid grid-cols-2 rounded-full bg-white p-1 text-sm font-semibold">
          {["student", "tutor"].map((r) => (
            <Link key={r} to={`/login/${r}`} data-testid={`login-tab-${r}`}
              className={`rounded-full py-2 text-center capitalize transition-colors ${portal === r ? "bg-[#0B1A33] text-white" : "text-slate-500"}`}>{r}</Link>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label>{isAdmin ? "Username or email" : "Email"}</Label>
          <Input required value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} className="h-11 bg-white" data-testid="login-identifier-input" />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><Label>Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#1F6FFF]" data-testid="login-forgot-link">Forgot password?</Link></div>
          <Input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 bg-white" data-testid="login-password-input" />
        </div>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-[#1F6FFF] font-semibold hover:bg-[#1450C9]" data-testid="login-submit-button">
          {busy && <Spinner />} Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
